import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import Spinner from "@/components/ui/Spinner";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const TimeseriesChart = ({
  data = [],
  isLoaded = false,
  error = null,
  title = "Timeseries Data",
  xKey = "key",
  series = [{ yKey: "value", label: "Count" }],
  chartType = "area",
  startDate = null,
  endDate = null,
  height = "300px",
}) => {
  const { t, i18n } = useTranslation();
  const seriesColors = useMemo(() => {
    const s = getComputedStyle(document.documentElement);
    const get = (v) => s.getPropertyValue(v).trim();
    return [get("--brand-color-dark"), get("--brand-color-1")].map((hex) => ({
      border: hex,
      bgArea: hexToRgba(hex, 0.1),
      bgBar: hexToRgba(hex, 0.7),
    }));
  }, []);

  const getDatesInRange = (start, end) => {
    const dates = [];
    let currentDate = new Date(start);
    const endDateTime = new Date(end);

    while (currentDate <= endDateTime) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  const formatDateToMatch = (date) => date.toISOString().split("T")[0];

  const processData = (rawData) => {
    if (!Array.isArray(rawData) || !rawData.length) return [];

    const sortedData = [...rawData].sort(
      (a, b) => new Date(a[xKey]) - new Date(b[xKey]),
    );

    const effectiveStartDate = startDate
      ? new Date(startDate)
      : new Date(sortedData[0][xKey]);

    const effectiveEndDate = endDate
      ? new Date(endDate)
      : new Date(sortedData[sortedData.length - 1][xKey]);

    const allDates = getDatesInRange(effectiveStartDate, effectiveEndDate);

    const dataMaps = series.map(
      ({ yKey }) =>
        new Map(
          sortedData.map((item) => [
            item[xKey],
            Number.isFinite(Number(item[yKey])) ? Number(item[yKey]) : 0,
          ]),
        ),
    );

    return allDates.map((date) => {
      const dateStr = formatDateToMatch(date);
      const entry = { [xKey]: dateStr };
      series.forEach(({ yKey }, i) => {
        entry[yKey] = dataMaps[i].get(dateStr) || 0;
      });
      return entry;
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.resolvedLanguage || i18n.language || "en-US", { month: "short", day: "numeric" });
  };

  const getSeriesColors = (index) => {
    const c = seriesColors[index % seriesColors.length];
    switch (chartType) {
      case "bar":
        return { backgroundColor: c.bgBar, borderColor: c.border };
      case "area":
        return { backgroundColor: c.bgArea, borderColor: c.border };
      default:
        return { borderColor: c.border, backgroundColor: "transparent" };
    }
  };

  const completeData = processData(data);

  const chartData = {
    labels: completeData.map((item) => formatDate(item[xKey])),
    datasets: series.map(({ yKey, label }, i) => ({
      label,
      data: completeData.map((item) => item[yKey]),
      ...getSeriesColors(i),
      borderWidth: 2,
      fill: chartType === "area" && i === 0,
      tension: chartType !== "bar" ? 0.3 : 0,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: series.length > 1,
        position: "top",
        labels: {
          font: { size: 11 },
          boxWidth: 12,
          padding: 8,
        },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${series[context.datasetIndex].label}: ${context.formattedValue}`,
          title: (contexts) =>
            formatDate(completeData[contexts[0].dataIndex][xKey]),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 45 },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f0f0f0" },
        ticks: { font: { size: 10 } },
      },
    },
  };

  const ChartComponent = chartType === "bar" ? Bar : Line;

  return (
    <div
      className="bg-white card-radius border h-100 d-flex flex-column"
      style={{ borderColor: "var(--border)" }}
    >
      {title && (
        <div className="px-3 py-2 border-bottom bg-light">
          <div className="text-sm fw-medium text-dark">{title}</div>
        </div>
      )}

      <div className="p-3">
        {error && (
          <div className="alert alert-danger mb-0" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {t(
              "common.loadError",
              "An error occurred while loading the data. Please try again later.",
            )}
          </div>
        )}

        {!error && !isLoaded && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: height }}
          >
            <Spinner />
          </div>
        )}

        {!error && isLoaded && data.length === 0 && (
          <div
            className="d-flex justify-content-center align-items-center text-muted"
            style={{ minHeight: height }}
          >
            <div>
              <i className="bi bi-graph-up me-2"></i>
              {t("common.noDataGeneric", "No data available")}
            </div>
          </div>
        )}

        {!error && isLoaded && data.length > 0 && (
          <div style={{ height }}>
            <ChartComponent data={chartData} options={options} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeseriesChart;
